import { Link, Navigate, useParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import { legalPolicies, type PolicySection } from "../data/legalPolicies";

function sectionAnchorId(section: PolicySection): string {
  if (section.anchorId) return section.anchorId;
  return section.heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
import { Seo } from "../components/Seo";

export function LegalPolicy() {
  const { slug } = useParams<{ slug: string }>();
  const doc = slug ? legalPolicies[slug] : undefined;

  if (!doc) {
    return <Navigate to="/legal" replace />;
  }

  const rawDesc = (doc.intro ? doc.intro : `Official ${doc.title} for DeskReplay.`)
    .replace(/\s+/g, " ")
    .trim();
  const metaDescription = rawDesc.length > 220 ? `${rawDesc.slice(0, 217)}...` : rawDesc;

  return (
    <>
      <Seo
        title={doc.title}
        description={metaDescription}
        canonicalPath={`/legal/${slug ?? ""}`}
      />
      <div className="bg-slate-950 min-h-screen pt-28 pb-24">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/legal"
          className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm font-medium mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          All legal policies
        </Link>

        <header className="mb-10 pb-8 border-b border-slate-800">
          <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-2">DeskReplay</p>
          <h1 className="text-slate-100 text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">{doc.title}</h1>
          <p className="text-on-dark-muted text-sm">Last updated: {doc.lastUpdated}</p>
        </header>

        {doc.intro && (
          <p className="text-on-dark-muted text-base leading-relaxed mb-10 whitespace-pre-line">{doc.intro}</p>
        )}

        <div className="flex flex-col gap-10">
          {doc.sections.map((section) => (
            <section key={section.heading} id={sectionAnchorId(section)}>
              <h2 className="text-slate-200 text-lg font-bold mb-4">{section.heading}</h2>
              <div className="flex flex-col gap-4">
                {section.paragraphs.map((p, i) => (
                  <p key={i} className="text-on-dark-muted text-[15px] leading-relaxed">
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
        </article>
      </div>
    </>
  );
}
